import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import Loading from "../../Components/Loading";
import CustomDataTable from "../../Components/CustomDataTable";
import { useDateFormatter } from "../../Components/FormatDateTime";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import "../../Styles/pages/customers.scss";
import "../../Styles/pages/dashboard.scss";

const getEnrichmentStatusKey = (row) => {
    if (row?.completedOn) {
        return "completed";
    }
    if (row?.pendingItemCount > 0) {
        return "processing";
    }
    return "pending";
};

const EnrichSubscribers = () => {
    const { t } = useTranslation("common");
    const { formatDateTime } = useDateFormatter();
    const navigate = useNavigate();

    const [state, setState] = useState({
        enrichments: [],
        first: 0,
        count: null,
        size: 20,
        sort: "createdOn,desc",
    });

    const defMembership = useSelector((s) => s.defMembership.defMembership);
    const customerEnrichments = useSelector((s) => s.businessCustomers.customerEnrichments);
    const fetchingCustomerEnrichments = useSelector((s) => s.businessCustomers.fetchingCustomerEnrichments);

    const dispatch = useDispatch();
    const getMembership = useCallback(() => dispatch(DefMembershipActions.defMembershipRequest()), [dispatch]);
    const getCustomerEnrichments = useCallback(
        (businessId, options) => dispatch(BusinessCustomersActions.customerEnrichmentsRequest(businessId, options)),
        [dispatch]
    );

    const isAdminOrOwner = defMembership?.role === "OWNER" || defMembership?.role === "ADMIN";

    const fetchEnrichments = useCallback(
        (page, rows = state.size) => {
            if (!defMembership?.business?.id) {
                return;
            }
            getCustomerEnrichments(defMembership.business.id, {
                page,
                size: rows,
                sort: state.sort,
            });
        },
        [defMembership?.business?.id, getCustomerEnrichments, state.size, state.sort]
    );

    useEffect(() => {
        if (!defMembership) {
            getMembership();
        }
    }, [defMembership, getMembership]);

    useEffect(() => {
        if (defMembership && !isAdminOrOwner) {
            navigate("/crm/customers", { replace: true });
        }
    }, [defMembership, isAdminOrOwner, navigate]);

    useEffect(() => {
        if (defMembership?.business?.id && isAdminOrOwner) {
            getCustomerEnrichments(defMembership.business.id, {
                page: 0,
                size: state.size,
                sort: state.sort,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defMembership?.business?.id, isAdminOrOwner]);

    useEffect(() => {
        if (customerEnrichments) {
            setState((prev) => ({
                ...prev,
                enrichments: customerEnrichments.content ?? [],
                count: customerEnrichments.totalElements ?? 0,
            }));
        }
    }, [customerEnrichments]);

    const onPageChange = (e) => {
        const { page, first, rows } = e;
        fetchEnrichments(page, rows);
        setState((prev) => ({
            ...prev,
            first,
            size: rows,
        }));
    };

    const statusBody = (rowData) => {
        const statusKey = getEnrichmentStatusKey(rowData);
        return (
            <span className={`enrichment-status enrichment-status-${statusKey}`}>
                {t(`business_customers.enrichment_status_${statusKey}`)}
            </span>
        );
    };

    const completedOnBody = (rowData) =>
        rowData.completedOn ? formatDateTime(rowData.completedOn) : "";

    const requestIdBody = (rowData) => (
        <span
            className="detail-navigation"
            onClick={() =>
                navigate(`/crm/enrichment-requests/${rowData.requestId}`, {
                    state: { enrichment: rowData },
                })
            }
        >
            {rowData.requestId}
        </span>
    );

    const columns = [
        {
            field: "requestId",
            header: t("business_customers.enrichment_request_id"),
            body: requestIdBody,
            headerClassName: "customer_headers datatable-header-default",
            alignHeader: "left",
        },
        {
            field: "status",
            header: t("business_customers.enrichment_status"),
            body: statusBody,
            headerClassName: "customer_headers datatable-header-default",
            alignHeader: "left",
        },
        {
            field: "entryCount",
            header: t("business_customers.enrichment_entry_count"),
            headerClassName: "customer_headers datatable-header-default",
            alignHeader: "left",
        },
        {
            field: "pendingItemCount",
            header: t("business_customers.enrichment_pending_count"),
            headerClassName: "customer_headers datatable-header-default",
            alignHeader: "left",
        },
        {
            field: "unmatchedCount",
            header: t("business_customers.enrichment_unmatched_count"),
            headerClassName: "customer_headers datatable-header-default",
            alignHeader: "left",
        },
        {
            field: "completedOn",
            header: t("business_customers.enrichment_completed_on"),
            body: completedOnBody,
            headerClassName: "customer_headers datatable-header-default",
            alignHeader: "left",
        },
    ];

    const tableHeader = (
        <div className="enrichment-table-header">
            <Button
                className="import_button"
                icon="pi pi-plus-circle"
                label={t("business_customers.upload_enrichment")}
                onClick={() => navigate("/crm/enrichment-requests/upload")}
            />
        </div>
    );

    if (!defMembership || !isAdminOrOwner) {
        return null;
    }

    if (fetchingCustomerEnrichments && !state.enrichments.length) {
        return <Loading />;
    }

    return (
        <div className="customerContent enrich-subscribers-screen">
            <div className="customers-screen">
                <CustomDataTable
                    value={state.enrichments}
                    sortField="createdOn"
                    sortOrder={-1}
                    paginator={false}
                    responsiveLayout="stack"
                    columns={columns}
                    dataKey="requestId"
                    header={tableHeader}
                    tableClassName="enrichment-data-table"
                    rowClassName={() => "subscribers-data-table enrichment-list-row"}
                    loading={fetchingCustomerEnrichments}
                />
                <Paginator
                    rows={state.size}
                    totalRecords={state.count ?? 0}
                    first={state.first}
                    onPageChange={onPageChange}
                    className="custom-paginator"
                />
            </div>
        </div>
    );
};

export default EnrichSubscribers;
